import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useSnackbar } from 'notistack';
import React from 'react';
import {
  type InstallationStages,
  type InstallationStageStatus,
  type ResponseGetJob,
  useUpdateInstallationStageStatus,
  useUpdateJobStep,
} from '../../../../app/ApiGen';
import { Dialog } from '../../../../components/Dialog';
import { errorSnackbar } from '../../../../components/ErrorSnackbar';
import { Text } from '../../../../components/UserDropdown/styled';
import { List, ListHeader, ListItem, Section } from '../../../../design-system';
import { CardTitle } from '../../../../design-system/Card/styled';
import {
  type CommissioningStepName,
  commissioningStepOrder,
  commissioningStepStatuses,
  getCurrentCommissioningStep,
  getCurrentInstallationStage,
  getOutstandingCommissioningSteps,
  HumanReadableCommissioningStep,
  HumanReadableInstallationStage,
  installationStageOrder,
  installationStageStatuses,
  type UpdatableInstallationStageName,
} from '../../../../utils/jobs/stages';

interface InstallationStagesSectionProps {
  canUpdate: boolean;
  installationStages: InstallationStages;
  jobId: string;
  refetchJob: () => Promise<ResponseGetJob | null>;
}

const StatusUpdateControl: React.VFC<{
  label: string;
  onUpdate: (status: InstallationStageStatus) => void;
  status: InstallationStageStatus;
  statuses: InstallationStageStatus[];
}> = ({ label, onUpdate, status, statuses }) => {
  const [selected, setSelected] = React.useState<InstallationStageStatus>(status);

  React.useEffect(() => setSelected(status), [status]);

  return (
    <Box display="flex" gap={1}>
      <Select
        inputProps={{ 'aria-label': `${label} status` }}
        onChange={(e) => setSelected(e.target.value as InstallationStageStatus)}
        size="small"
        style={{ minWidth: 150 }}
        value={selected}
        variant="outlined"
      >
        {statuses.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
      <Button
        aria-label={`Update ${label}`}
        color="primary"
        disabled={selected === status}
        onClick={() => onUpdate(selected)}
        variant="contained"
      >
        Update
      </Button>
    </Box>
  );
};

const StageRow: React.VFC<{
  action?: React.ReactNode;
  isCurrent: boolean;
  isStep?: boolean;
  label: string;
  status: InstallationStageStatus;
}> = ({ action, isCurrent, isStep = false, label, status }) => (
  <ListItem>
    <Grid alignItems="center" container>
      <Grid item xs={3}>
        <Box pl={isStep ? 3 : 0}>{label}</Box>
      </Grid>
      <Grid item xs={2}>
        {status}
      </Grid>
      <Grid item xs={2}>
        {isCurrent && <Chip color="primary" label="Current" size="small" />}
      </Grid>
      <Grid item xs={5}>
        {action}
      </Grid>
    </Grid>
  </ListItem>
);

const CompleteStepsDialog: React.VFC<{
  isOpen: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: () => void;
  steps: CommissioningStepName[];
}> = ({ isOpen, isSubmitting, onClose, onConfirm, steps }) => (
  <Dialog
    hasCancelButton
    hasCloseButton={false}
    isOpen={isOpen}
    onClose={onClose}
    submitButton={
      <Button color="warning" disabled={isSubmitting} onClick={onConfirm} variant="contained">
        Mark steps as completed
      </Button>
    }
    title="Complete commissioning steps"
  >
    <p>The following commissioning steps will be marked as Completed:</p>
    <ul>
      {steps.map((step) => (
        <li key={step}>{HumanReadableCommissioningStep[step]}</li>
      ))}
    </ul>
    <p>
      This does not commission the charge point or change the Commissioning stage status. Use Move Job To Manually
      Commissioned once the steps are completed.
    </p>
  </Dialog>
);

export const InstallationStagesSection: React.VFC<InstallationStagesSectionProps> = ({
  canUpdate,
  installationStages,
  jobId,
  refetchJob,
}) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { mutate: mutateStage } = useUpdateInstallationStageStatus({ jobId, stage: 'preWorkForm' });
  const { mutate: mutateStep } = useUpdateJobStep({ jobId, step: 'wifi' });
  const [isCompleteStepsDialogOpen, setCompleteStepsDialogOpen] = React.useState(false);
  const [isCompletingSteps, setCompletingSteps] = React.useState(false);

  const currentStage = getCurrentInstallationStage(installationStages);
  const currentStep = getCurrentCommissioningStep(installationStages);
  const outstandingSteps = getOutstandingCommissioningSteps(installationStages);

  const updateStage = (stage: UpdatableInstallationStageName, status: InstallationStageStatus) => {
    const label = HumanReadableInstallationStage[stage];
    mutateStage({ data: { id: jobId, type: 'jobs', attributes: { status } } }, { pathParams: { jobId, stage } })
      .then(async () => {
        await refetchJob();
        enqueueSnackbar(`${label} updated to "${status}"`, { variant: 'success' });
      })
      .catch((err) => {
        errorSnackbar(`Unable to update ${label}`, closeSnackbar, enqueueSnackbar, err);
      });
  };

  const updateStep = async (step: CommissioningStepName, status: InstallationStageStatus) =>
    mutateStep({ data: { type: 'job-steps', attributes: { status } } }, { pathParams: { jobId, step } });

  const handleUpdateStep = (step: CommissioningStepName, status: InstallationStageStatus) => {
    const label = HumanReadableCommissioningStep[step];
    updateStep(step, status)
      .then(async () => {
        await refetchJob();
        enqueueSnackbar(`${label} updated to "${status}"`, { variant: 'success' });
      })
      .catch((err) => {
        errorSnackbar(`Unable to update ${label}`, closeSnackbar, enqueueSnackbar, err);
      });
  };

  const handleCompleteOutstandingSteps = () => {
    let pendingStep: CommissioningStepName | undefined;
    setCompletingSteps(true);
    outstandingSteps
      .reduce(async (previous, step) => {
        await previous;
        pendingStep = step;
        await updateStep(step, 'Completed');
      }, Promise.resolve())
      .then(() => {
        enqueueSnackbar('Commissioning steps marked as completed', { variant: 'success' });
      })
      .catch((err) => {
        const label = pendingStep ? HumanReadableCommissioningStep[pendingStep] : 'commissioning steps';
        errorSnackbar(`Unable to mark ${label} as completed`, closeSnackbar, enqueueSnackbar, err);
      })
      .then(async () => {
        setCompletingSteps(false);
        setCompleteStepsDialogOpen(false);
        await refetchJob();
      })
      .catch(() => undefined);
  };

  return (
    <Section>
      <CardTitle>
        Installation Stages
        {canUpdate && <Text>The Commissioning stage status cannot be changed here, only its steps.</Text>}
      </CardTitle>
      <List inset variant="bordered">
        <ListHeader>
          <Grid container>
            <Grid item xs={3}>
              Stage
            </Grid>
            <Grid item xs={4}>
              Status
            </Grid>
            <Grid item xs={5}>
              {canUpdate && 'Action'}
            </Grid>
          </Grid>
        </ListHeader>
        {installationStageOrder.map((stage) => (
          <React.Fragment key={stage}>
            <StageRow
              action={
                canUpdate &&
                (stage === 'commissioning' ? (
                  outstandingSteps.length > 0 && (
                    <Button color="warning" onClick={() => setCompleteStepsDialogOpen(true)} variant="contained">
                      Complete outstanding steps
                    </Button>
                  )
                ) : (
                  <StatusUpdateControl
                    label={HumanReadableInstallationStage[stage]}
                    onUpdate={(status) => updateStage(stage, status)}
                    status={installationStages[stage].status}
                    statuses={installationStageStatuses}
                  />
                ))
              }
              isCurrent={stage === currentStage}
              label={HumanReadableInstallationStage[stage]}
              status={installationStages[stage].status}
            />
            {stage === 'commissioning' &&
              commissioningStepOrder.map((step) => (
                <StageRow
                  key={step}
                  action={
                    canUpdate && (
                      <StatusUpdateControl
                        label={HumanReadableCommissioningStep[step]}
                        onUpdate={(status) => handleUpdateStep(step, status)}
                        status={installationStages.commissioning.steps[step].status}
                        statuses={commissioningStepStatuses}
                      />
                    )
                  }
                  isCurrent={step === currentStep}
                  isStep
                  label={HumanReadableCommissioningStep[step]}
                  status={installationStages.commissioning.steps[step].status}
                />
              ))}
          </React.Fragment>
        ))}
      </List>
      <Box m={1} />
      <CompleteStepsDialog
        isOpen={isCompleteStepsDialogOpen}
        isSubmitting={isCompletingSteps}
        onClose={() => setCompleteStepsDialogOpen(false)}
        onConfirm={handleCompleteOutstandingSteps}
        steps={outstandingSteps}
      />
    </Section>
  );
};
